import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styles from './PublicationCaseStudy.module.css';

export default function AIAnomalyPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.articleContainer}>

      <button className={styles.backButton} onClick={() => navigate('/research')}>
        ← Back to Publications
      </button>

      {/* Publication Metadata */}
      <div className={styles.metaGrid}>
        <div className={styles.metaCard}>
          <div className={styles.metaLabel}>Paper ID</div>
          <div className={styles.metaValue}>A030</div>
        </div>
        <div className={styles.metaCard}>
          <div className={styles.metaLabel}>ISBN</div>
          <div className={styles.metaValue}>979-8-3315-6839-9</div>
        </div>
        <div className={styles.metaCard}>
          <div className={styles.metaLabel}>IEEE Catalog</div>
          <div className={styles.metaValue}>CFP25VB5-USB</div>
        </div>
        <div className={styles.metaCard}>
          <div className={styles.metaLabel}>Indexing</div>
          <div className={styles.metaValue}>IEEE Xplore, EI Compendex, Scopus</div>
        </div>
      </div>

      <header className={styles.header}>
        <h1 className={styles.title}>
          A Novel AI-Driven Anomaly Detection Framework for Predictive Maintenance of Aircraft Engines
        </h1>
        <div className={styles.authors}>
          Md Hemal Ishak, <span className={styles.highlightAuthor}>Jolok Banarjee</span>, Arobi Sultana Oishy, Saifur Rahman Bakaul<sup>*</sup>
        </div>
        <div className={styles.affiliations}>
          Department of Aerospace Engineering, Aviation and Aerospace University, Bangladesh<br />
          <sup>*</sup>Corresponding author: saif@aaub.edu.bd
        </div>
      </header>

      {/* Availability Notice */}
      <div className={styles.noticeBox}>
        <h3 className={styles.noticeTitle}>Publication Status</h3>
        <p className={styles.noticeText}>
          <strong>Published and awarded</strong> the Best Presentation Paper at MEAE 2025. The conference proceedings are published by IEEE and indexed in <strong>EI Compendex</strong> and <strong>Scopus</strong>. The full paper is available online via IEEE Xplore (DOI: <a href="https://doi.org/10.1109/MEAE68077.2025.11557878" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>10.1109/MEAE68077.2025.11557878</a>).
        </p>
      </div>

      <div className={styles.abstractBox}>
        <h3 className={styles.abstractTitle}>Abstract</h3>
        <p className={styles.abstractText}>
          Predictive maintenance (PdM) utilizes data analysis and machine learning to monitor the equipment condition by detecting anomalies. Traditional reactive maintenance is expensive and often leads to unplanned downtime when unexpected failures occur. PdM overcomes these limitations, minimizing downtime, lowering maintenance expenses, and extending machinery life. This paper presents a Python-based machine-learning framework specifically designed for anomaly detection in aircraft engines to enable predictive maintenance. The framework uses an ensemble-based Isolation Forest algorithm applied to NASA C-MAPSS turbofan engine datasets. Model performance was evaluated using standard classification metrics and graphical visualization, and compared with several widely used anomaly detection algorithms. The results demonstrate that the proposed approach is superior for detecting anomalies in high-dimensional, complex datasets compared to conventional methods.
        </p>
        <div className={styles.keywords}>
          <strong>Keywords:</strong> predictive maintenance; isolation forest; machine learning; anomaly detection; C-MAPSS; turbofan engine
        </div>
      </div>

      {/* Key Metrics */}
      <div className={styles.metricsGrid}>
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>86%</div>
          <div className={styles.metricLabel}>Case-1 Accuracy (FD001)</div>
        </div>
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>83%</div>
          <div className={styles.metricLabel}>Case-2 Accuracy (FD001–FD004)</div>
        </div>
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>0.88</div>
          <div className={styles.metricLabel}>Case-1 ROC AUC</div>
        </div>
        <div className={styles.metricCard}>
          <div className={styles.metricValue}>0.83</div>
          <div className={styles.metricLabel}>Case-2 ROC AUC</div>
        </div>
      </div>

      <section>
        <h2 className={styles.sectionTitle}>Summary</h2>

        <h3 className={styles.subSectionTitle}>Problem & Approach</h3>
        <p className={styles.paragraph}>
          Traditional anomaly detection methods struggle with high-dimensional, nonlinear engine sensor data. We developed an unsupervised framework using <strong>Isolation Forest</strong> on NASA C-MAPSS turbofan datasets (FD001–FD004), comparing it against LOF, One-Class SVM, and Elliptic Envelope.
        </p>

        <h3 className={styles.subSectionTitle}>Key Findings</h3>
        <ul className={styles.bulletList}>
          <li>Isolation Forest achieved the highest accuracy on the large, multi-condition dataset (Case-2: 83%), while OC-SVM degraded from 89% to 61% as complexity increased.</li>
          <li>Hyperparameter tuning (Bayesian Optimization, Grid Search, Randomized Search) improved Case-1 accuracy from ~83% to 86%.</li>
          <li>ROC AUC remained strong across both cases (0.88 and 0.83), confirming robustness against class imbalance.</li>
          <li>Feature selection identified 10 critical sensors (2, 3, 4, 7, 11, 12, 15, 17, 20, 21) via correlation and statistical analysis.</li>
        </ul>

        <h3 className={styles.subSectionTitle}>Impact</h3>
        <p className={styles.paragraph}>
          Early anomaly detection reduces unscheduled maintenance costs, minimizes operational downtime, and enhances flight safety. The lightweight, classical-ML approach is ideal for real-time edge deployment and integration into next-generation digital twin architectures.
        </p>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Framework & Results</h2>

        <figure className={styles.singleFigure}>
          <img
            src="/assets/publication/anomaly_detection_turbofan/anomaly detection framework.png"
            alt="Anomaly Detection Framework"
          />
          <figcaption>
            Fig. 1. End-to-end pipeline: EDA → Pre-processing → Feature Selection → Isolation Forest → Hyperparameter Tuning → Evaluation
          </figcaption>
        </figure>

        <figure className={styles.singleFigure}>
          <img
            src="/assets/publication/anomaly_detection_turbofan/ROC Curve for Case-1 (FD001) & Case-2 (FD001-FD004) after Hyperparameter Tuning.png"
            alt="ROC Curve"
          />
          <figcaption>
            Fig. 2. ROC Curves after tuning: Case-1 AUC = 0.88, Case-2 AUC = 0.83
          </figcaption>
        </figure>

        <figure className={styles.singleFigure}>
          <img
            src="/assets/publication/anomaly_detection_turbofan/Precision-Recall Curve for all Models of Case-2  after Hyperparameter Tuning.png"
            alt="Precision-Recall Comparison"
          />
          <figcaption>
            Fig. 3. Comparative Precision-Recall curves (Case-2): Isolation Forest (AP=0.62) outperforms LOF, OC-SVM, and Elliptic Envelope on complex multi-dataset scenarios
          </figcaption>
        </figure>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Conclusion</h2>
        <p className={styles.paragraph}>
          Isolation Forest demonstrates superior scalability and reliability for turbofan anomaly detection across small and large datasets compared to boundary-based alternatives. Future work will explore hybrid neural-network ensembles to further improve early fault identification for digital twin and predictive maintenance systems.
        </p>
      </section>

      {/* Citation */}
      <div className={styles.citationBox}>
        <h3 className={styles.noticeTitle}>How to Cite</h3>
        <p className={styles.citationText}>
          H. Ishak, J. Banarjee, A. S. Oishy, and S. R. Bakaul, "A Novel AI-Driven Anomaly Detection Framework for Predictive Maintenance of Aircraft Engines," <em>2025 11th International Conference on Mechanical Engineering and Aerospace Engineering</em>, Beijing, China, IEEE Xplore, 2025. DOI: 10.1109/MEAE68077.2025.11557878
        </p>
      </div>

      <div className={styles.noticeBox}>
        <h3 className={styles.noticeTitle}>This Work is Part of Undergraduate Thesis</h3>
        <p className={styles.noticeText} style={{ marginBottom: '0.85rem' }}>
          This anomaly detection framework is a core component of the broader End-to-End Predictive Maintenance (E2E PdM) system. Explore the full thesis including Bi-LSTM condition monitoring and LSTM RUL prediction.
        </p>
        <Link
          to="/projects/e2e-pdm-framework"
          className={styles.btnPrimary}
        >
          View Project Page →
        </Link>
      </div>

    </div>
  );
}